import { getLocalizedMessage } from '@figma/i18n/get_localized_message'
import { t } from '@figma/i18n/translation'
import type { I18nServerObject } from '@figma/i18n/types'
import { generalAnErrorOccurredWhilePerformingThatAction } from '@figma/i18n-dictionaries/en/web/general'
import { atomStore } from '@figma/jotai'
import { type AsyncAction, type ThunkCreator, thunkCreator } from '@web/lib/action_helpers'
import type { JSX } from 'react'

import { flashesAtom } from './atoms'
import { type FlashInitialOptions, FlashStatus } from './types'

var flashId = 0

/**
 * Given a server-generated string or JSON-parsed i18n object, returns a localized string
 * if possible. Otherwise, returns the fallback text or server-generated string.
 *
 * When localizing surfaces that used to pass information from Sinatra to React in plaintext
 * (such as flash messages), we must identify whether the server is sending an i18n object while
 * maintaining backward-compatibility with unlocalized plaintext messages.
 *
 * @param i18n_or_raw_text An i18n object or raw text passed from the server to the frontend.
 * @returns a localized string if possible. Otherwise, returns the plaintext fallback string or
 * string passed from the server
 */
function getPlaintextOrLocalizedMessage(i18n_or_raw_text: I18nServerObject | string) {
  if (typeof i18n_or_raw_text === 'string') {
    return i18n_or_raw_text
  } else {
    return getLocalizedMessage({ data: { i18n: i18n_or_raw_text } }, i18n_or_raw_text.fallback_text)
  }
}

const showFlashWithStatus = /*#__PURE__*/ thunkCreator<
  {
    id: number
    message: string | JSX.Element
    status: FlashStatus
    timeout?: number
    dark?: boolean
    persistent?: boolean
    icon?: string
    bottom?: boolean
  },
  number
>((store, payload) => {
  // remove any duplicate flashes if they exist
  const flashes = atomStore.get(flashesAtom)
  for (const id in flashes) {
    const flash = flashes[id]
    if (flash?.message === payload.message && flash.status === payload.status) {
      store.dispatch(removeFlash({ id: parseInt(id) }))
      break
    }
  }

  const id = payload.id
  const timeout =
    payload.timeout && payload.timeout > FLASH_TIMEOUT ? payload.timeout : FLASH_TIMEOUT
  if (!payload.persistent) {
    setTimeout(() => {
      store.dispatch(removeFlash({ id }))
    }, timeout)
  }
  store.dispatch(addFlash(payload))

  return id
})

export const flashOnFailure: ThunkCreator<{
  promise: Promise<unknown>
  fallbackError: string
}> = /*#__PURE__*/ thunkCreator<{
  promise: Promise<unknown>
  fallbackError: string
}>((store, payload) => {
  payload.promise.catch(({ response }) => {
    try {
      response = JSON.parse(response)
      const message = getLocalizedMessage(
        { ...response, data: { ...response.data, i18n: response.i18n } },
        response.message,
      )
      store.dispatch(showErrorFlash(message))
    } catch (e) {
      store.dispatch(
        showErrorFlash(payload.fallbackError || t(generalAnErrorOccurredWhilePerformingThatAction)),
      )
    }
  })
})

export const addFlash: ThunkCreator<{
  id: number
  message: string | JSX.Element
  status: FlashStatus
  timeout?: number
  dark?: boolean
  persistent?: boolean
  icon?: string
  bottom?: boolean
}> = /*#__PURE__*/ thunkCreator<{
  id: number
  message: string | JSX.Element
  status: FlashStatus
  timeout?: number
  dark?: boolean
  persistent?: boolean
  icon?: string
  bottom?: boolean
}>((_, payload) => {
  atomStore.set(flashesAtom, { ...atomStore.get(flashesAtom), [payload.id]: payload })
})

export const removeFlash: ThunkCreator<{ id: number }> = /*#__PURE__*/ thunkCreator<{
  id: number
}>((_, payload) => {
  const newState = { ...atomStore.get(flashesAtom) }
  delete newState[payload.id]
  atomStore.set(flashesAtom, newState)
})

export const removeAllFlashes: ThunkCreator<void> = /*#__PURE__*/ thunkCreator<void>(() => {
  atomStore.set(flashesAtom, {})
})

// Default time after the flash will fade away
export const FLASH_TIMEOUT = 3000
// A longer timeout to wait for the page to finish loading,
// giving a bit longer to absorb the page first before fading.
export const SERVER_SIDE_FLASH_TIMEOUT = 6000

export const initializeFlash: ThunkCreator<FlashInitialOptions['flash']> =
  /*#__PURE__*/ thunkCreator<FlashInitialOptions['flash']>((store, serverFlash) => {
    if (serverFlash) {
      if (serverFlash.error) {
        store.dispatch(
          showErrorFlash(
            getPlaintextOrLocalizedMessage(serverFlash.error),
            SERVER_SIDE_FLASH_TIMEOUT,
          ),
        )
      } else if (serverFlash.warn) {
        store.dispatch(
          showFlash(
            getPlaintextOrLocalizedMessage(serverFlash.warn),
            SERVER_SIDE_FLASH_TIMEOUT,
            serverFlash.dark,
            serverFlash.persistent,
            serverFlash.icon,
            serverFlash.bottom,
          ),
        )
      } else if (serverFlash.success) {
        store.dispatch(
          showFlash(
            getPlaintextOrLocalizedMessage(serverFlash.success),
            SERVER_SIDE_FLASH_TIMEOUT,
            serverFlash.dark,
            serverFlash.persistent,
            serverFlash.icon,
            serverFlash.bottom,
          ),
        )
      }
    }
  })

export function showFlash(
  content: string | JSX.Element,
  timeout?: number,
  dark?: boolean,
  persistent?: boolean,
  icon?: string,
  bottom?: boolean,
): AsyncAction<number, unknown, unknown> {
  return showFlashWithStatus({
    message: content,
    status: FlashStatus.DEFAULT,
    timeout,
    dark,
    persistent,
    icon,
    bottom,
    id: flashId++,
  })
}

export function showErrorFlash(
  message: string | JSX.Element,
  timeout?: number,
  dark?: boolean,
  persistent?: boolean,
  icon?: string,
  bottom?: boolean,
): AsyncAction<number, unknown, unknown> {
  return showFlashWithStatus({
    message,
    status: FlashStatus.ERROR,
    timeout,
    dark,
    persistent,
    icon,
    bottom,
    id: flashId++,
  })
}
